import { useParams } from 'react-router-dom'
import { FileText, Download, Upload } from 'lucide-react'
import { useDocuments } from '@/hooks/useDocuments'
import { CATEGORY_LABELS, CATEGORY_CONTENT_TYPE } from '@/lib/config'
import { timeAgo, formatBytes } from '@/lib/utils'
import type { Document } from '@/types'

export default function CategoryPage() {
  const { slug = '' } = useParams<{ slug: string }>()
  const { documents, loading } = useDocuments({ category: slug })

  const label = CATEGORY_LABELS[slug] ?? slug
  const contentType = CATEGORY_CONTENT_TYPE[slug]

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800 dark:text-white">{label}</h1>
        {contentType && (
          <span className="text-xs bg-cerah-teal-light text-cerah-teal px-2 py-0.5 rounded-full font-medium mt-1 inline-block">
            {contentType.replace('_', ' ')}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : documents.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="flex flex-col gap-3">
          {documents.map(doc => <DocumentCard key={doc.id} doc={doc} />)}
        </div>
      )}
    </div>
  )
}

function DocumentCard({ doc }: { doc: Document }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 flex items-start gap-4 hover:border-cerah-teal/30 transition-colors">
      <div className="w-10 h-10 rounded-lg bg-cerah-teal-light flex items-center justify-center shrink-0">
        <FileText size={20} className="text-cerah-teal" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 dark:text-white">{doc.title}</p>
        {doc.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{doc.description}</p>
        )}
        <div className="flex items-center gap-3 mt-2">
          <span className="text-[10px] text-gray-400">{timeAgo(doc.created_at)}</span>
          {doc.file_name && <span className="text-[10px] text-gray-400">{doc.file_name}</span>}
          {doc.file_size && <span className="text-[10px] text-gray-400">{formatBytes(doc.file_size)}</span>}
          {doc.tags?.map(tag => (
            <span key={tag} className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded">
              {tag}
            </span>
          ))}
        </div>
      </div>
      {doc.file_url && (
        <a
          href={doc.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 flex items-center gap-1.5 text-xs text-cerah-teal hover:underline"
        >
          <Download size={14} /> Unduh
        </a>
      )}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Upload size={36} className="text-gray-300 dark:text-gray-600 mb-3" />
      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Belum ada dokumen</p>
      <p className="text-xs text-gray-400 mt-1">Dokumen yang diupload akan muncul di sini.</p>
    </div>
  )
}
